/* ═══════════════════════════════════════════════════════════════
   PRIYA'S EMAIL REPLIES — Keyed by inbox IDs (mki1...mki5)
   Drafted by the relevant agent (Marsi, Wright, Buzz, Luc).
   Merged with EMAIL_REPLIES in V4ChatPanel / V4MarketingBriefing.
   ═══════════════════════════════════════════════════════════════ */
export const PRIYA_EMAIL_REPLIES = {
  mki1: {
    agent: 'marsi',
    subject: 'Re: LinkedIn Carousel — slides 3 & 5 feedback',
    recipient: 'Neha Gupta',
    thinkingTime: 8,
    thinkingSteps: [
      { label: 'Thinking...', detail: "Reviewing Neha's carousel draft + Wright's thesis alignment" },
      { label: 'Pulling draft...', detail: "Cross-referencing slides against the Thursday launch checklist" },
      { label: 'Drafting response...', detail: "Framing targeted feedback on slides 3 and 5" },
    ],
    intro: "Neha's designs are solid overall — honest, on-brand. Two slides need a directional nudge, not a redo. Keep the feedback concrete so she can turn it fast.",
    draft: `Hi Neha,

Designs look great overall — the 6-slide flow has real momentum. Two specific asks before Thursday:

**Slide 3** — the copy leans product-y right now. Can we reframe it toward Wright's thesis? Something like *"Automate right, not most — here's what that looks like in practice"* would land stronger than the feature-led version. Same visual, tighter headline.

**Slide 5** — go with newsletter signup as the CTA, not webinar register. We're in awareness mode for this audience, and the newsletter conversion rate on this persona is 4x webinar signups (Dora confirmed).

Everything else is approved. If you can get the revised v2 to me by EOD Wednesday, we're good to launch Thursday 9 AM.

Best,
Priya`,
    draftPrompt: "This is the right tone — direct but collaborative. Want to send as-is, or edit the slide 3 framing?",
    attachments: [
      { name: 'Carousel_v1_annotated.pdf', type: 'pdf', size: '2.4 MB' },
    ],
    sentConfirm: "Sent. Neha typically turns feedback within the hour — expect v2 by EOD Wednesday. 4 more emails in the stack — keep going?",
    draftConfirm: "Saved to drafts. Annotations preserved so Neha has context when you come back to it.",
    remindConfirm: "Got it, I'll nudge you {time} — the Thursday launch date is non-negotiable so worth keeping this warm.",
    editScenario: {
      userMsg: "Soften the slide 3 feedback — I don't want to make her feel like she missed the brief. Frame it as a refinement, not a miss.",
      thinkingTime: 4,
      lucResponse: "Good read — Neha's nailed every brief this quarter, no reason to make this feel corrective. I've reframed slide 3 as a refinement rather than a rewrite.",
      revisedDraft: `Hi Neha,

Designs look great overall — the 6-slide flow has real momentum. Two small refinements before Thursday:

**Slide 3** — everything works, just wondering if we can pull Wright's thesis into the headline? Something like *"Automate right, not most — here's what that looks like in practice"* would tie the carousel back to the blog post we're launching alongside it. Same visual, tighter copy.

**Slide 5** — go with newsletter signup as the CTA, not webinar register. We're in awareness mode for this audience, and the newsletter conversion rate on this persona is 4x webinar signups (Dora confirmed).

Everything else is approved. If you can get the revised v2 to me by EOD Wednesday, we're good to launch Thursday 9 AM.

Best,
Priya`,
    },
  },

  mki2: {
    agent: 'marsi',
    subject: 'Re: Meta campaign report — pause Ad Set B',
    recipient: 'Raj Bhandari',
    thinkingTime: 10,
    thinkingSteps: [
      { label: 'Thinking...', detail: "Pulling Raj's report + Meta campaign live metrics" },
      { label: 'Cross-referencing...', detail: "Comparing reallocation recommendation against our own analysis" },
      { label: 'Drafting response...', detail: "Aligning Raj's ask with the broader channel reallocation plan" },
    ],
    intro: "Raj's call is right but narrow. Worth going further — pause B, narrow A to VP+, move the full $800 to LinkedIn. Reply should thank him, give him the fuller plan, and align on the 2 PM deadline.",
    draft: `Hi Raj,

Aligned on pausing Ad Set B — appreciate the sharp read on the 5-day data.

Going slightly further than your recommendation, with Marsi's concurrence:

**1. Pause Ad Set B immediately** — confirmed, you're right, it's cratering blended CTR.
**2. Narrow Ad Set A targeting** to VP+ only (dropping generic SMB). Historical VP+ CTR is 3%+, which gets us closer to target.
**3. Reallocate the full $800 to LinkedIn top performer** — same destination you suggested, slightly larger budget because we free up the narrowed A audience.

Marsi is pushing the changes now. You'll see them reflected in the dashboard by 1 PM, ahead of your 2 PM deadline. We've set a 48-hour checkpoint on Ad Set A — if CTR doesn't lift above 3%, we pause A as well and go fully to LinkedIn.

Thanks for the clean report. Let's reconnect Friday on the week-over-week.

Best,
Priya`,
    draftPrompt: "This respects Raj's work while going slightly further. Send it?",
    attachments: [
      { name: 'Channel_Reallocation_v2.xlsx', type: 'spreadsheet', size: '180 KB' },
    ],
    sentConfirm: "Sent. Raj will appreciate the directness — he's a veteran and prefers fast alignment over a volley of questions.",
    draftConfirm: "Saved to drafts. Ad Set B pause still triggers on your approval — draft doesn't block execution.",
    remindConfirm: "Got it, I'll nudge you {time}. Meanwhile I'll hold Ad Set B changes pending your sign-off.",
    editScenario: {
      userMsg: "Make it a bit shorter — Raj reads on mobile and long emails get skimmed. Keep the 3-point plan but trim the intro and outro.",
      thinkingTime: 3,
      lucResponse: "Tightened. Three-point plan intact, half the word count. Reads cleanly on mobile now.",
      revisedDraft: `Hi Raj,

Agreed on pausing Ad Set B — sharp read on the 5-day data.

Going slightly further, with Marsi's concurrence:

1. **Pause Ad Set B immediately** — confirmed.
2. **Narrow Ad Set A to VP+ only** (drop generic SMB) — historical CTR 3%+.
3. **Reallocate $800 to LinkedIn top performer** — same destination, larger budget.

Marsi's pushing the changes now — live in the dashboard by 1 PM. 48-hour checkpoint on A; if CTR doesn't hit 3%, we pause it too and go fully to LinkedIn.

Clean report. Friday sync for the WoW.

Best,
Priya`,
    },
  },

  mki3: {
    agent: 'luc',
    subject: 'Re: Content calendar summary for 2 PM investor meeting',
    recipient: 'Arjun Mehta',
    thinkingTime: 9,
    thinkingSteps: [
      { label: 'Thinking...', detail: "Pulling together the product-launch content plan for Arjun" },
      { label: 'Cross-referencing...', detail: "Coordinating with Wright and Buzz on the distribution plan" },
      { label: 'Drafting response...', detail: "Framing blog cadence + social + community activation cleanly for investor review" },
    ],
    intro: "Arjun needs this tight — investor read, no fluff. I'll pull the blog cadence, social distribution, and dev community activation into one page. Direct, structured, no marketing jargon.",
    draft: `Hey Arjun,

Sending ahead of your 2 PM. Here's the launch content plan:

**Blog cadence (5 weeks)**
- Week -4: Launch teaser + founder POV (Wright writing)
- Week -3: Technical deep-dive on product architecture (co-written with Stark)
- Week -2: Customer story — 3 beta users, quantified outcomes
- Week -1: Feature walkthrough with demo video
- Launch Week: Full product announcement + pricing

**Social distribution**
- LinkedIn: 18 posts across the 5 weeks (Neha's designs, Wright's copy)
- Twitter: 22 posts with 3 scheduled AMA sessions
- Threads: experimental coverage — 2-week trial

**Developer community activation**
- Week -2: Public beta opens to our 1,400-person Builders Slack
- Week -1: 2 developer podcasts confirmed (AI Builders Show + Platform Weekly)
- Launch Week: 3 community champions doing live-build streams

All content landing on time. The developer activation is the piece I'd highlight for the investor conversation — it's the motion that differentiates us.

Let me know if you want deeper detail on any track before 2 PM.

— Priya`,
    draftPrompt: "Clean, investor-readable. Send it or want to add the ROI angle?",
    attachments: [
      { name: 'Launch_Content_Plan.pdf', type: 'pdf', size: '420 KB' },
    ],
    sentConfirm: "Sent. Arjun will have time to scan before his 2 PM. The developer activation callout is the hook.",
    draftConfirm: "Saved to drafts. I'll keep the PDF linked so you can send fast from mobile.",
    remindConfirm: "Got it, I'll nudge you {time}. Flagging Arjun so he knows the doc is coming.",
    editScenario: {
      userMsg: "Add one line about projected reach — Arjun will want the number for his investor conversation.",
      thinkingTime: 3,
      lucResponse: "Added. Combined organic + paid reach projection at the top of the plan — gives Arjun a number to anchor the conversation.",
      revisedDraft: `Hey Arjun,

Sending ahead of your 2 PM. Here's the launch content plan:

**Projected reach:** 280K+ combined organic + paid impressions across the 5-week run, with ~6K qualified engagements.

**Blog cadence (5 weeks)**
- Week -4: Launch teaser + founder POV (Wright writing)
- Week -3: Technical deep-dive on product architecture (co-written with Stark)
- Week -2: Customer story — 3 beta users, quantified outcomes
- Week -1: Feature walkthrough with demo video
- Launch Week: Full product announcement + pricing

**Social distribution**
- LinkedIn: 18 posts across the 5 weeks (Neha's designs, Wright's copy)
- Twitter: 22 posts with 3 scheduled AMA sessions
- Threads: experimental coverage — 2-week trial

**Developer community activation**
- Week -2: Public beta opens to our 1,400-person Builders Slack
- Week -1: 2 developer podcasts confirmed (AI Builders Show + Platform Weekly)
- Launch Week: 3 community champions doing live-build streams

Developer activation is the piece I'd highlight for the investor conversation — it's the motion that differentiates us.

— Priya`,
    },
  },

  mki4: {
    agent: 'wright',
    subject: 'Re: TechCorp case study for re-engagement',
    recipient: 'David Chen',
    thinkingTime: 11,
    thinkingSteps: [
      { label: 'Thinking...', detail: "Pulling Sally's TechCorp context + comparable customer stories" },
      { label: 'Cross-referencing...', detail: "Matching profile: enterprise + API-heavy + clear ROI" },
      { label: 'Drafting response...', detail: "Framing case study selection + turnaround timeline" },
    ],
    intro: "David needs this by Wednesday. The right customer is clear — Acme Corp fits the profile exactly. Reply should commit to the case study, lock in format, and align on the Wednesday EOD delivery.",
    draft: `Hey David,

Aligned. The customer story that matches TechCorp's profile best is Acme Corp — they're enterprise, API-heavy, and we have quantified outcomes you can lean on.

**What I'm drafting:**
- One-page PDF case study (mobile-friendly for email attachment)
- Focus: 3 quantified outcomes (time savings, uptime improvement, cost reduction)
- Direct quote from Acme's CTO (already cleared with their team)
- Technical architecture diagram showing the API integration depth

**Timeline:**
- First draft to you by Tuesday EOD
- Wednesday morning revision if you have edits
- Final delivery Wednesday noon — ahead of your outreach sequence

Let me know if you want a specific angle pulled forward (ROI, time-to-value, technical depth) based on TechCorp's current objections. Wright can tune the lead quote to match.

Best,
Priya`,
    draftPrompt: "Lean, committed, timeline locked. Send it?",
    attachments: [
      { name: 'Acme_Corp_Case_Study_outline.pdf', type: 'pdf', size: '380 KB' },
    ],
    sentConfirm: "Sent. David typically replies within 2 hours if he has preferences on angle.",
    draftConfirm: "Saved to drafts. Wright will start the Acme draft whether you send or not — delivery stays on Wednesday.",
    remindConfirm: "Got it, I'll nudge you {time}. Wright's starting the draft regardless so timeline holds.",
    editScenario: {
      userMsg: "Ask David specifically what TechCorp's current objections are — I don't want to guess the angle.",
      thinkingTime: 3,
      lucResponse: "Added a direct ask for TechCorp's current objections. Better than guessing the angle; David will appreciate it.",
      revisedDraft: `Hey David,

Aligned. The customer story that matches TechCorp's profile best is Acme Corp — they're enterprise, API-heavy, and we have quantified outcomes you can lean on.

**Before I commit the angle:** what are TechCorp's current objections in the re-engagement conversations? Pricing, technical depth, integration concerns, or something else? Knowing that lets Wright tune the case study lead quote and structure to push on the right lever.

**What I'm drafting (regardless):**
- One-page PDF case study (mobile-friendly for email attachment)
- Three quantified outcomes (time savings, uptime improvement, cost reduction)
- Direct quote from Acme's CTO (pre-cleared)
- Technical architecture diagram

**Timeline:**
- First draft to you by Tuesday EOD
- Wednesday morning revision window
- Final delivery Wednesday noon — ahead of your outreach

Ping me with the TechCorp objection read whenever easy.

Best,
Priya`,
    },
  },

  mki5: {
    agent: 'wright',
    subject: 'Re: Blog post SEO analysis',
    recipient: 'Wright',
    thinkingTime: 7,
    thinkingSteps: [
      { label: 'Thinking...', detail: "Reviewing Wright's SEO recommendations + keyword analysis" },
      { label: 'Cross-referencing...', detail: "Checking keyword alignment with the Tuesday publish schedule" },
      { label: 'Drafting response...', detail: "Confirming implementation approach for title + H2 updates" },
    ],
    intro: "Wright's SEO report is solid — 3 real keyword opportunities and a stronger title option. Quick alignment reply. Keep it tight.",
    draft: `Wright,

Quick alignment before you apply the SEO updates:

**Title:** Go with "AI Ops for Growing Teams: Automate Right, Not Most." Anchors the thesis in the headline. Stronger than the current.

**H2 keywords:** All three — apply them to H2s in Sections 2, 3, and 4. Keep the writing natural, don't force keyword density.

**Meta description:** Rewrite to include the primary keyword, but keep the voice punchy. Target 155 characters.

One addition — can you also check internal linking? The post should link back to the Acme case study once it's published next week. Set up the link target now so it's live on day one.

Implement and queue for Tuesday 2 PM publish.

Best,
Priya`,
    draftPrompt: "Crisp internal note to Wright — direct, scope-clear. Send it?",
    attachments: [],
    sentConfirm: "Sent. Wright will implement and queue for Tuesday. Internal linking noted — he'll ping you when the Acme case study link target is ready.",
    draftConfirm: "Saved to drafts. Wright is standing by, will start only when you send.",
    remindConfirm: "Got it, I'll nudge you {time}. Wright knows publish time is Tuesday 2 PM.",
    editScenario: {
      userMsg: "Add a note asking Wright to track which keyword drives the most traffic — I want to calibrate future pieces off that signal.",
      thinkingTime: 3,
      lucResponse: "Added the tracking ask. This is a smart compounding move — future SEO work gets sharper with each piece.",
      revisedDraft: `Wright,

Quick alignment before you apply the SEO updates:

**Title:** Go with "AI Ops for Growing Teams: Automate Right, Not Most." Anchors the thesis in the headline.

**H2 keywords:** All three — apply to H2s in Sections 2, 3, and 4. Keep the writing natural, don't force density.

**Meta description:** Rewrite to include the primary keyword, punchy voice, target 155 characters.

**Internal linking:** Point the post back to the Acme case study target once it's live next week — set the link up now so it's live day one.

**One more ask:** track which of the three keywords drives the most traffic over the first 14 days. I want to use that signal to calibrate future pieces — future SEO work gets sharper with each data point.

Implement and queue for Tuesday 2 PM publish.

Best,
Priya`,
    },
  },
};
